import { ROLE_LEVELS } from '@/types/user';
import type {
  BookingStatus,
  DeliverySlot,
  BookingPaymentMethod,
  ReminderChannel,
} from './types';

export { ROLE_LEVELS };

export type BadgeVariant = 'success' | 'warning' | 'draft' | 'danger' | 'active' | 'pending' | 'info';

export const STATUS_BADGE: Record<BookingStatus, { label: string; variant: BadgeVariant; tint: string }> = {
  PENDING: {
    label: 'Pending',
    variant: 'pending',
    tint: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800/60 dark:text-neutral-200',
  },
  CONFIRMED: {
    label: 'Confirmed',
    variant: 'info',
    tint: 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-200',
  },
  READY: {
    label: 'Ready',
    variant: 'active',
    tint: 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-200',
  },
  DELIVERED: {
    label: 'Delivered',
    variant: 'success',
    tint: 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-200',
  },
  CANCELLED: {
    label: 'Cancelled',
    variant: 'danger',
    tint: 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-200',
  },
};

export const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'READY', label: 'Ready' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export const SLOT_OPTIONS: Array<{ value: DeliverySlot | ''; label: string }> = [
  { value: '', label: 'Any slot' },
  { value: 'MORNING', label: 'Morning' },
  { value: 'AFTERNOON', label: 'Afternoon' },
  { value: 'EVENING', label: 'Evening' },
];

export const SLOT_LABEL: Record<DeliverySlot, string> = {
  MORNING: 'Morning',
  AFTERNOON: 'Afternoon',
  EVENING: 'Evening',
};

export const SLOT_HOURS: Record<DeliverySlot, string> = {
  MORNING: '9:00 — 12:00',
  AFTERNOON: '13:00 — 16:00',
  EVENING: '18:00 — 21:00',
};

/**
 * `delivery_slot` now holds a specific pickup time ("HH:MM"). Legacy bookings
 * may still carry the old MORNING/AFTERNOON/EVENING slot codes, so this formats
 * either: a known slot → its label, a "HH:MM" time → 12-hour clock, else raw.
 */
export function formatDeliveryTime(value?: string | null): string {
  if (!value) return '';
  if (value in SLOT_LABEL) return SLOT_LABEL[value as DeliverySlot];
  const m = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (m) {
    const h = Number(m[1]);
    const ampm = h < 12 ? 'AM' : 'PM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${m[2]} ${ampm}`;
  }
  return value;
}

export const PAYMENT_METHOD_OPTIONS: Array<{ value: BookingPaymentMethod; label: string }> = [
  { value: 'CASH', label: 'Cash' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CARD', label: 'Card' },
];

export const REMINDER_CHANNELS: Array<{ value: ReminderChannel; label: string }> = [
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'SMS', label: 'SMS' },
];

export const REMINDER_TYPE_LABEL: Record<string, string> = {
  '48_HOUR': '48-hour notice',
  '24_HOUR': '24-hour notice',
  PICKUP_DAY: 'Pickup-day reminder',
  MANUAL: 'Manual reminder',
};

export const PAGE_SIZE = 20;

export function formatBookingDate(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(iso);
  }
}

export function formatBookingDateTime(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return String(iso);
  }
}

export function depositPercent(advance: number, total: number): number {
  if (!total || total <= 0) return 0;
  return Math.min(100, Math.round((advance / total) * 100));
}

export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}
